import React from 'react';
import { oneOfType, object, bool, func, shape, string, node, array } from 'prop-types';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { intlShape, injectIntl, FormattedMessage } from '../../util/reactIntl';
import classNames from 'classnames';
import { propTypes } from '../../util/types';
import { maxLength, required, composeValidators } from '../../util/validators';
import {
  Form,
  PrimaryButtonInline,
  FieldTextInput,
  FieldSelect,
  FieldMultiSelectInput,
  PreviewListingButton,
} from '../../components';

import css from './EditLiveRoomDetailsForm.module.css';

const TITLE_MAX_LENGTH = 60;
const MENU_PLACEMENT = 'top';

// Multi select max options
const AGE_MAX_OPTIONS = 2;
const LANGUAGE_MAX_OPTIONS = 2;

/**
 * Formats the FieldMultiSelect placeholder from the given
 * array of options.
 *
 * @param {array} options
 *
 * @return {string} option label
 */
const multiSelectPlaceholder = options => options[0].label;

const EditLiveRoomDetailsFormComponent = props => {
  return (
    <FinalForm
      {...props}
      render={formRenderProps => {
        const {
          className,
          disabled,
          ready,
          handleSubmit,
          intl,
          invalid,
          pristine,
          saveActionMsg,
          updated,
          updateInProgress,
          fetchErrors,
          panelTitle,
          listing,
          categoryOptions,
          themeOptions,
          ageOptions,
          languageOptions,
        } = formRenderProps;

        const titleMessage = intl.formatMessage({ id: 'EditLiveRoomDetailsForm.title' });
        const titlePlaceholderMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.titlePlaceholder',
        });
        const titleRequiredMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.titleRequired',
        });
        const maxLengthMessage = intl.formatMessage(
          { id: 'EditLiveRoomDetailsForm.maxLength' },
          {
            maxLength: TITLE_MAX_LENGTH,
          }
        );

        const descriptionMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.description',
        });
        const descriptionPlaceholderMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.descriptionPlaceholder',
        });
        const maxLength60Message = maxLength(maxLengthMessage, TITLE_MAX_LENGTH);
        const descriptionRequiredMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.descriptionRequired',
        });

        const categoryMessage = intl.formatMessage({ id: 'EditLiveRoomDetailsForm.category' });
        const categoryRequiredMessage = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.categoryRequired',
        });

        const themeMessage = intl.formatMessage({ id: 'EditLiveRoomDetailsForm.theme' });

        const ageMessage = intl.formatMessage({ id: 'EditLiveRoomDetailsForm.age' });
        const agePlaceholder = multiSelectPlaceholder(ageOptions);

        const languageMessage = intl.formatMessage({ id: 'EditLiveRoomDetailsForm.language' });
        const languagePlaceholder = multiSelectPlaceholder(languageOptions);

        const selectInputPlaceholder = intl.formatMessage({
          id: 'EditLiveRoomDetailsForm.selectPlaceholder',
        });

        const { updateListingError, createListingDraftError } = fetchErrors || {};
        const errorMessageUpdateListing = updateListingError ? (
          <p className={css.error}>
            <FormattedMessage id="EditLiveRoomDetailsForm.updateFailed" />
          </p>
        ) : null;

        // This error happens only on first tab (of EditListingWizard)
        const errorMessageCreateListingDraft = createListingDraftError ? (
          <p className={css.error}>
            <FormattedMessage id="EditLiveRoomDetailsForm.createListingDraftError" />
          </p>
        ) : null;

        const classes = classNames(css.root, className);
        const submitReady = (updated && pristine) || ready;
        const submitInProgress = updateInProgress;
        const submitDisabled = invalid || disabled || submitInProgress;

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div className={css.content}>
              <div className={css.contentWrapper}>
                <h1 className={css.title}>{panelTitle}</h1>
                {errorMessageCreateListingDraft}
                {errorMessageUpdateListing}
                <FieldTextInput
                  id="title"
                  name="title"
                  type="text"
                  label={titleMessage}
                  placeholder={titlePlaceholderMessage}
                  maxLength={TITLE_MAX_LENGTH}
                  validate={composeValidators(required(titleRequiredMessage), maxLength60Message)}
                />

                <div className={css.fieldsFlex}>
                  <FieldSelect
                    id="category"
                    name="category"
                    className={css.field}
                    options={categoryOptions}
                    label={categoryMessage}
                    placeholder={selectInputPlaceholder}
                    validate={required(categoryRequiredMessage)}
                  />
                  <FieldSelect
                    id="theme"
                    name="theme"
                    className={css.field}
                    options={themeOptions}
                    label={themeMessage}
                    placeholder={selectInputPlaceholder}
                  />
                </div>

                <FieldTextInput
                  id="description"
                  name="description"
                  className={css.field}
                  type="textarea"
                  label={descriptionMessage}
                  placeholder={descriptionPlaceholderMessage}
                  validate={composeValidators(required(descriptionRequiredMessage))}
                />

                <div className={css.fieldsFlex}>
                  <FieldMultiSelectInput
                    id="age"
                    name="age"
                    className={css.field}
                    label={ageMessage}
                    placeholder={agePlaceholder}
                    options={ageOptions}
                    maxOptions={AGE_MAX_OPTIONS}
                    menuPlacement={MENU_PLACEMENT}
                  />
                  <FieldMultiSelectInput
                    id="language"
                    name="language"
                    className={css.field}
                    label={languageMessage}
                    placeholder={languagePlaceholder}
                    options={languageOptions}
                    maxOptions={LANGUAGE_MAX_OPTIONS}
                    menuPlacement={MENU_PLACEMENT}
                  />
                </div>
              </div>
            </div>

            <div className={css.submitButtonRoot}>
              <PreviewListingButton listing={listing} />
              <PrimaryButtonInline
                size="small"
                checkmarkSize="small"
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={submitReady}
              >
                {saveActionMsg}
              </PrimaryButtonInline>
            </div>
          </Form>
        );
      }}
    />
  );
};

EditLiveRoomDetailsFormComponent.defaultProps = {
  className: null,
  fetchErrors: null,
  panelTitle: null,
};

EditLiveRoomDetailsFormComponent.propTypes = {
  className: string,
  intl: intlShape.isRequired,
  onSubmit: func.isRequired,
  saveActionMsg: string.isRequired,
  disabled: bool.isRequired,
  ready: bool.isRequired,
  updated: bool.isRequired,
  updateInProgress: bool.isRequired,
  panelTitle: oneOfType([object, node]),
  fetchErrors: shape({
    createListingDraftError: propTypes.error,
    updateListingError: propTypes.error,
  }),

  // filter configs
  categoryOptions: array.isRequired,
  themeOptions: array.isRequired,
  ageOptions: array.isRequired,
  languageOptions: array.isRequired,
};

export default compose(injectIntl)(EditLiveRoomDetailsFormComponent);
